import React, { useCallback, useEffect, useState } from "react"
import classNames from "classnames"
import { District, UserRole, Worker } from "../../../../types"
import { Button } from "../../../components/button/Button"
import panelstyles from "../AdminTasks/sidepanel.module.css"
import { getDistricts } from "../../../fetch/districts"
import { DeleteIcon, IconButton } from "../../../components/button/IconButton"
import { TenantDetails } from "../../../store/tenant"
import { Form } from "@/components/ui/form"
import { InputField } from "@/components/ui/form/InputField"
import styled from "styled-components"
import { DropdownField } from "@/components/ui/form/DropdownField"
import { useForm, useWatch } from "react-hook-form"
import { toast } from "react-toastify"
import { usersApi } from "@/fetch/api/users"

const StyledForm = styled.form`
  max-width: 750px;
  margin: 32px auto 0;
  gap: 8px;
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Container = styled.div`
  margin-bottom: 32px;
`

type FormInputs = {
  email: string
  username: string
  name: string
  role: UserRole[]
  coeficient: number
  permissionsDistricts: number[]
}

type Props = {
  data?: Worker
  onClose: (updated?: boolean) => void
  onEditUser(data: any): Promise<void>
  features?: TenantDetails["settings"]["features"]
}
export const EditUser = ({ data, onClose, onEditUser, features }: Props) => {
  return (
    <div
      className={classNames(
        panelstyles.sidepanel,
        data ? panelstyles.open : ""
      )}
    >
      {data?.id ? (
        <EditUserForm
          data={data}
          onClose={onClose}
          features={features}
          onEditUser={onEditUser}
        />
      ) : null}
    </div>
  )
}

type EditUserFormProps = {
  data: Worker
  onClose: (updated?: boolean) => void
  onEditUser(data: any): Promise<void>
  features?: TenantDetails["settings"]["features"]
}
export const EditUserForm = ({
  data,
  onClose,
  onEditUser,
  features,
}: EditUserFormProps) => {
  const [loading, setLoading] = useState(false)
  const form = useForm<FormInputs>({
    defaultValues: {
      email: data.email,
      username: data.username,
      name: data.name,
      role: [data.role],
      coeficient: data.coeficient,
      permissionsDistricts:
        data.permissions?.districts?.map(({ id }) => id) ?? [],
    },
  })
  const role = useWatch({
    control: form.control,
    name: "role",
  })
  const [districts, setDistricts] = useState<District[]>([])
  const hasFeatureDistricts = features?.["districts"]
  const roleOptions = [
    { id: UserRole.User, name: "Darbinieks" },
    ...(hasFeatureDistricts
      ? [{ id: UserRole.Manager, name: "📋 Menedžeris" }]
      : []),
    { id: UserRole.Admin, name: "✅ Administrators" },
  ]

  const init = () => {
    getDistricts().then(async (res) => {
      const json = await res.json()
      if (json) {
        setDistricts(json)
      }
    })
  }

  useEffect(() => {
    init()
  }, [])

  const onDelete = useCallback(async () => {
    setLoading(true)
    try {
      await usersApi.deleteUser(data.id)
    } catch (error) {
      setLoading(false)
      toast("Darbība nav izdevusies, lūdzu mēģiniet vēlreiz", { type: "error" })
    }
    onClose(true)
  }, [setLoading, onClose])

  const resetPassword = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setLoading(true)
    try {
      const response = await usersApi.resetUserPasswordUrl(data.id, true)
      toast("Jauns paroles atjaunošanas links izveidots", { type: "success" })
    } catch (error) {
      console.error(error)
      toast("Neizdevās izveidot jaunu linku, lūdzu mēģiniet vēlreiz", {
        type: "error",
      })
    } finally {
      // wait 1 second before closing
      await (() => new Promise((resolve) => setTimeout(resolve, 1000)))()
      setLoading(false)
      onClose(true)
    }
  }

  const onSubmit = async (values: FormInputs) => {
    setLoading(true)
    const { email, role, name, username, coeficient, permissionsDistricts } =
      values
    let hasError = false
    if (!name) {
      form.setError("name", { type: "required" })
      hasError = true
    }
    if (!username) {
      form.setError("username", { type: "required" })
      hasError = true
    }
    if (coeficient < 0) {
      form.setError("coeficient", { type: "required" })
      hasError = true
    }
    if (!email) {
      form.setError("email", { type: "required" })
      hasError = true
    } else if (!email.includes("@")) {
      form.setError("email", { type: "required" })
      hasError = true
    }
    if (!role?.[0]) {
      form.setError("role", { type: "required" })
      hasError = true
    } else if (
      (role?.[0] === UserRole.User || role?.[0] === UserRole.Manager) &&
      !permissionsDistricts?.length
    ) {
      form.setError("permissionsDistricts", { type: "required" })
      hasError = true
    }

    if (hasError) {
      setLoading(false)
      return
    }

    try {
      await onEditUser({
        email,
        username,
        name,
        coeficient: +coeficient || 0,
        role: role?.[0],
        permissions: {
          projects: [],
          opGroups: [],
          districts: permissionsDistricts.map((id) => ({
            id,
            name: districts.find((d) => d.id === id)?.name || "",
          })),
        },
      })
    } catch (err) {
      setLoading(false)
      toast("Darbība nav izdevusies, lūdzu mēģiniet vēlreiz", { type: "error" })
    }
  }

  return (
    <div className={classNames(panelstyles.wrap, panelstyles.flexCol)}>
      <div className={panelstyles.close}>
        <Button variant="inline" onClick={() => onClose()}>
          Aizvērt
        </Button>
      </div>
      <Form {...form}>
        <StyledForm onSubmit={form.handleSubmit(onSubmit)}>
          <Container>
            <InputField
              control={form.control}
              name="email"
              placeholder="E-pasts"
              label="E-pasts"
              error={form.formState.errors.email}
            />
            <InputField
              control={form.control}
              name="username"
              placeholder="Lietotājvārds"
              label="Lietotājvārds"
              error={form.formState.errors.username}
            />
            <InputField
              control={form.control}
              name="name"
              placeholder="Vārds, Uzvārds"
              label="Vārds, Uzvārds"
              error={form.formState.errors.name}
            />
            <InputField
              control={form.control}
              name="coeficient"
              placeholder="Koeficients"
              label="Koeficients"
              error={form.formState.errors.coeficient}
              inputProps={{ type: "number" }}
            />
            <DropdownField
              control={form.control}
              name="role"
              placeholder="Piekļuves tiesības"
              label="Piekļuves tiesības"
              options={roleOptions}
            />
            {[UserRole.User, UserRole.Manager].includes(Number(role?.[0])) &&
              features?.["districts"] && (
                <DropdownField
                  control={form.control}
                  name="permissionsDistricts"
                  placeholder="Iecirkņi"
                  label="Iecirkņi"
                  multiple
                  options={[
                    { id: undefined, name: "Neviens" },
                    ...districts.map(({ name, id }) => ({
                      id,
                      name,
                    })),
                  ]}
                />
              )}
          </Container>
          <div className={panelstyles.buttons}>
            <IconButton onClick={onDelete} disabled={loading}>
              {DeleteIcon}
            </IconButton>
            <div>
              <Button
                variant="danger"
                onClick={resetPassword}
                disabled={loading}
              >
                Atjaunot paroli
              </Button>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={() => onClose()}>
                Atcelt
              </Button>
              <Button type="submit" disabled={loading}>
                Saglabāt
              </Button>
            </div>
          </div>
        </StyledForm>
      </Form>
    </div>
  )
}
